import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Snackbar, Alert } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useEntitlement } from './useEntitlement';
import { DEFAULT_TENANT_ID } from './TenantSelector';

interface EntitlementGuardProps {
  domainId: string;
  children: React.ReactNode;
}

export function EntitlementGuard({ domainId, children }: EntitlementGuardProps) {
  const { hasEntitlement, isLoading } = useEntitlement(DEFAULT_TENANT_ID);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' });

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">Loading permissions...</Typography>
      </Box>
    );
  }

  if (!hasEntitlement(domainId)) {
    return (
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          textAlign: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 400 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Access Required
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This feature ({domainId}) is not enabled for your organisation. Contact your administrator to request access.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSnackbar({ open: true, message: 'Access request submitted. Your administrator will be notified.', severity: 'info' });
            }}
          >
            Request Access
          </Button>
        </Stack>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return <>{children}</>;
}
